// src/components/Charts/ChartContainer.jsx
import React, { useEffect, useRef, useState } from "react";
import { ResponsiveContainer } from "recharts";

/**
 * ✅ Fixes Recharts "width(-1) height(-1)" when charts mount in hidden/zero-size containers
 * + ✅ Allows a fallback UI when there's no data (renders OUTSIDE recharts)
 */
const ChartContainer = ({ title, icon: Icon, height = 300, children, fallback }) => {
  const h = Number(height) > 0 ? Number(height) : 300;

  const wrapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [rcKey, setRcKey] = useState(0);

  // If fallback is provided, we do NOT need recharts mounting at all
  const showFallback = Boolean(fallback);

  useEffect(() => {
    if (showFallback) return; // ✅ skip ResizeObserver/recharts when fallback is used

    const el = wrapRef.current;
    if (!el) return;

    let raf1 = 0;
    let raf2 = 0;

    const check = () => {
      const w = el.clientWidth;
      const hh = el.clientHeight;

      if (w > 0 && hh > 0) {
        setReady(true);
        setRcKey((k) => k + 1);
      } else {
        setReady(false);
      }
    };

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(check);
    });

    const ro = new ResizeObserver(() => check());
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [showFallback]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {Icon ? <Icon className="h-5 w-5 text-gray-700 dark:text-gray-300" /> : null}
          {title}
        </h2>
      </div>

      <div className="px-4 py-4">
        <div ref={wrapRef} className="w-full min-w-0 relative" style={{ height: h, minHeight: h }}>
          {showFallback ? (
            // ✅ Render fallback directly (no recharts, no axis line, no weird width)
            <div className="absolute inset-0 flex items-center justify-center px-6">
              {fallback}
            </div>
          ) : ready ? (
            <ResponsiveContainer key={rcKey} width="100%" height="100%" debounce={150}>
              {children}
            </ResponsiveContainer>
          ) : (
            <div className="h-full w-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              Loading chart...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartContainer;